import React, { useEffect } from "react";
import Section from "../../../components/section/Section";
import ItemSelector from "../../../components/question/question-preview/ItemSelector";
import useEvaluateConstraint from "../../../components/question/question-preview/hooks/useEvaluateConstraint";
import { formatStepNumber, getSectionTitle } from "./steps";
import { content } from "./content";

const LooseItem = ({ item, hasErrors, submissionId }) => (
   <div className="preview-survey-section--questions_wrapper">
      <ItemSelector hasErrors={hasErrors} submissionId={submissionId} props={item.surveyItem} />
   </div>
);

const StepHeader = ({ step, progress }) => {
   const title = getSectionTitle(step.section);

   if (step.isIntro) {
      return (
         <header className="tr-step__header tr-step__header--intro">
            <h2 className="tr-step__intro-title" tabIndex={-1}>
               {title}
            </h2>
         </header>
      );
   }

   return (
      <header className="tr-step__header">
         <span className="tr-step__number" aria-hidden="true">
            {formatStepNumber(step.number)}
         </span>
         <h2 className="tr-step__title" tabIndex={-1}>
            {title}
         </h2>
         {progress.total > 0 && (
            <>
               <div
                  className="tr-step__progress"
                  role="progressbar"
                  aria-valuemin={0}
                  aria-valuemax={progress.total}
                  aria-valuenow={progress.position}
                  aria-label={content.card.progressLabel(progress.position, progress.total)}
               >
                  <span style={{ width: `${(progress.position / progress.total) * 100}%` }} />
               </div>
               <span className="tr-step__fraction">
                  {progress.position}/{progress.total}
               </span>
            </>
         )}
      </header>
   );
};

/**
 * One top-level section of the survey. Inactive steps stay mounted - only hidden - because the
 * submit, draft and print code read every rendered question from the DOM.
 */
const SurveyStep = ({
   step,
   isActive,
   progress,
   hasErrors,
   submissionId,
   onVisibilityChange,
   stepRef
}) => {
   // The same skip-logic check Section makes for itself, so the card knows to skip this step.
   const relevant = step.section?.surveyItem?.relevant;
   const isRelevant = useEvaluateConstraint(relevant, true);
   const isVisible = !relevant || Boolean(isRelevant) || Boolean(submissionId);

   useEffect(() => {
      onVisibilityChange(step.key, isVisible);
   }, [step.key, isVisible, onVisibilityChange]);

   return (
      <section
         ref={stepRef}
         className={`tr-step${step.isIntro ? " tr-step--intro" : ""}${
            isActive ? " tr-step--active" : ""
         }${isVisible ? "" : " tr-step--skipped"}`}
         aria-hidden={!isActive}
      >
         {isVisible && step.section && <StepHeader step={step} progress={progress} />}
         <div className="tr-step__body">
            {step.leadingItems.map((item) => (
               <LooseItem
                  key={item.id}
                  item={item}
                  hasErrors={hasErrors}
                  submissionId={submissionId}
               />
            ))}
            {step.section && (
               <Section
                  surveyItem={step.section}
                  hasErrors={hasErrors}
                  submissionId={submissionId}
               />
            )}
            {step.trailingItems.map((item) => (
               <LooseItem
                  key={item.id}
                  item={item}
                  hasErrors={hasErrors}
                  submissionId={submissionId}
               />
            ))}
         </div>
      </section>
   );
};

export default SurveyStep;
